import { FormEvent, useEffect, useRef, useState } from "react"
import { config } from "../lib/config"

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: { sitekey: string; callback: (token: string) => void }) => string
      reset: (widgetId: string) => void
    }
  }
}

type ContactSectionProps = {
  id?: string
}

export const ContactSection = ({ id = "contato" }: ContactSectionProps) => {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [message, setMessage] = useState("")
  const [turnstileToken, setTurnstileToken] = useState("")
  const [status, setStatus] = useState<"idle" | "loading" | "ok" | "error">("idle")
  const [feedback, setFeedback] = useState("")
  const turnstileRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string>("")

  useEffect(() => {
    if (!config.turnstileSiteKey || !turnstileRef.current) return

    const script = document.createElement("script")
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
    script.async = true
    script.onload = () => {
      if (!turnstileRef.current || !window.turnstile) return
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: config.turnstileSiteKey,
        callback: (token: string) => setTurnstileToken(token),
      })
    }
    document.body.appendChild(script)

    return () => {
      script.remove()
    }
  }, [])

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setStatus("loading")
    setFeedback("")

    try {
      if (config.turnstileSiteKey) {
        if (!turnstileToken) {
          throw new Error("Confirme o desafio anti-bot")
        }
        const verify = await fetch("/api/public/turnstile/verify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: turnstileToken }),
        })
        if (!verify.ok) {
          throw new Error("Verificação Turnstile falhou")
        }
      }

      const subject = encodeURIComponent(`[${config.appName}] Demonstração — ${name}`)
      const body = encodeURIComponent(`Nome: ${name}\nE-mail: ${email}\n\n${message}`)
      window.location.href = `mailto:${config.supportEmail}?subject=${subject}&body=${body}`
      setStatus("ok")
      setFeedback("Abrimos seu cliente de e-mail. Envie a mensagem para concluir.")
    } catch (error) {
      setStatus("error")
      setFeedback(error instanceof Error ? error.message : "Não foi possível enviar")
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current)
      }
      setTurnstileToken("")
    }
  }

  return (
    <section id={id} className="bg-white py-16 sm:py-20">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 lg:grid-cols-2">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">Solicitar demonstração</h2>
          <p className="mt-3 text-slate-600">
            Planos comerciais em breve. Fale com nosso time para condições, homologação e integração API.
          </p>
          <p className="mt-6 text-sm text-slate-500">
            Ou envie direto para{" "}
            <a href={`mailto:${config.supportEmail}`} className="font-medium text-brand-600 hover:underline">
              {config.supportEmail}
            </a>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-6">
          <div>
            <label htmlFor="contact-name" className="text-sm font-medium text-slate-700">
              Nome
            </label>
            <input
              id="contact-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </div>
          <div>
            <label htmlFor="contact-email" className="text-sm font-medium text-slate-700">
              E-mail
            </label>
            <input
              id="contact-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </div>
          <div>
            <label htmlFor="contact-message" className="text-sm font-medium text-slate-700">
              Mensagem
            </label>
            <textarea
              id="contact-message"
              required
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </div>
          {config.turnstileSiteKey ? <div ref={turnstileRef} /> : null}
          <button
            type="submit"
            disabled={status === "loading"}
            className="w-full rounded-lg bg-brand-600 px-4 py-3 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
          >
            {status === "loading" ? "Enviando..." : "Solicitar demonstração"}
          </button>
          {feedback ? (
            <p className={`text-sm ${status === "error" ? "text-red-600" : "text-green-700"}`}>{feedback}</p>
          ) : null}
        </form>
      </div>
    </section>
  )
}
